import React, { useState, useEffect, useCallback } from "react";
import {
  Wrench,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Type,
  Link2,
  Image,
  Mail,
  Loader2,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  RefreshCw,
  Zap,
} from "lucide-react";
import api from "../api/client";

const FIX_TYPES = {
  meta_descriptions: {
    label: "Meta Descriptions",
    icon: FileText,
    color: "text-blue-400",
    bg: "bg-blue-900/20",
  },
  title_tags: {
    label: "Title Tags",
    icon: Type,
    color: "text-purple-400",
    bg: "bg-purple-900/20",
  },
  orphan_pages: {
    label: "Orphan Pages",
    icon: Link2,
    color: "text-amber-400",
    bg: "bg-amber-900/20",
  },
  alt_text: {
    label: "Alt Text",
    icon: Image,
    color: "text-green-400",
    bg: "bg-green-900/20",
  },
  weekly_report: {
    label: "Weekly Report",
    icon: Mail,
    color: "text-cyan-400",
    bg: "bg-cyan-900/20",
  },
};

const STATUS_BADGE = {
  running: { color: "text-yellow-400 bg-yellow-900/20", icon: Loader2, spin: true },
  completed: { color: "text-green-400 bg-green-900/20", icon: CheckCircle },
  failed: { color: "text-red-400 bg-red-900/20", icon: XCircle },
  suggested: { color: "text-blue-400 bg-blue-900/20", icon: Clock },
  pushed: { color: "text-green-400 bg-green-900/20", icon: CheckCircle },
  approved: { color: "text-green-400 bg-green-900/20", icon: Check },
  rejected: { color: "text-gray-500 bg-gray-900/20", icon: X },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.suggested;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon size={12} className={cfg.spin ? "animate-spin" : ""} />
      {status}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color = "text-copper" }) {
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color.replace("text-", "bg-")}/10`}>
          <Icon size={20} className={color} />
        </div>
        <div>
          <div className="text-2xl font-bold text-white font-mono">{value}</div>
          <div className="text-xs text-gray-500">{label}</div>
        </div>
      </div>
    </div>
  );
}

function TaskCard({ type, latestRun, enabled, onTrigger, onFix, triggering, pendingCount }) {
  const cfg = FIX_TYPES[type] || FIX_TYPES.meta_descriptions;
  const Icon = cfg.icon;
  const hasIssues = latestRun && latestRun.items_found > 0 && latestRun.items_found > latestRun.items_fixed;
  const unfixed = latestRun ? latestRun.items_found - latestRun.items_fixed : 0;

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${cfg.bg}`}>
            <Icon size={18} className={cfg.color} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">{cfg.label}</h3>
            <span className={`text-xs ${enabled ? "text-green-400" : "text-gray-600"}`}>
              {enabled ? "Enabled" : "Disabled"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onTrigger(type)}
            disabled={triggering === type || triggering === `fix_${type}`}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-dark-50 hover:border-copper text-gray-400 hover:text-copper rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
          >
            {triggering === type ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RefreshCw size={12} />
            )}
            Scan
          </button>
          {(hasIssues || pendingCount > 0) && type !== "weekly_report" && (
            <button
              onClick={() => onFix(type)}
              disabled={triggering === `fix_${type}` || triggering === type}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-copper hover:bg-copper-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
            >
              {triggering === `fix_${type}` ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Wrench size={12} />
              )}
              Fix {pendingCount > 0 ? pendingCount : unfixed} Issues
            </button>
          )}
        </div>
      </div>
      {latestRun ? (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <StatusBadge status={latestRun.status} />
            <span className="text-gray-500 font-mono">
              {new Date(latestRun.started_at).toLocaleDateString()}
            </span>
          </div>
          <div className="flex gap-4 text-xs text-gray-400">
            <span>Found: <span className="text-white font-mono">{latestRun.items_found}</span></span>
            <span>Fixed: <span className="text-green-400 font-mono">{latestRun.items_fixed}</span></span>
            {unfixed > 0 && (
              <span>Pending: <span className="text-amber-400 font-mono">{unfixed}</span></span>
            )}
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-600">No runs yet — click Scan to detect issues</div>
      )}
    </div>
  );
}

function SuggestionRow({ item, onApprove, onReject, approving }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-dark-50 last:border-0">
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-dark-100/50">
        <button onClick={() => setExpanded(!expanded)} className="text-gray-500">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">{item.page_title || item.page_url}</div>
          <div className="text-xs text-gray-500 truncate">{item.page_url}</div>
        </div>
        <StatusBadge status={item.status} />
        <span className="text-xs text-gray-500 px-2 py-0.5 bg-dark-100 rounded">
          {item.fix_type.replace("_", " ")}
        </span>
        {item.status === "suggested" && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onApprove(item.id)}
              disabled={approving}
              className="p-1.5 rounded-lg bg-green-900/20 text-green-400 hover:bg-green-900/40 transition-colors"
              title="Approve & Push"
            >
              <Check size={14} />
            </button>
            <button
              onClick={() => onReject(item.id)}
              disabled={approving}
              className="p-1.5 rounded-lg bg-red-900/20 text-red-400 hover:bg-red-900/40 transition-colors"
              title="Reject"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>
      {expanded && (
        <div className="px-12 pb-3 space-y-2">
          {item.current_value && (
            <div>
              <span className="text-xs text-gray-500">Current:</span>
              <div className="text-xs text-red-400 font-mono bg-red-900/10 rounded p-2 mt-1">
                {item.current_value}
              </div>
            </div>
          )}
          <div>
            <span className="text-xs text-gray-500">Suggested:</span>
            <div className="text-xs text-green-400 font-mono bg-green-900/10 rounded p-2 mt-1">
              {item.suggested_value}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SeoMaintenance({ selectedSite }) {
  const [dashboard, setDashboard] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(null);
  const [approving, setApproving] = useState(false);
  const [tab, setTab] = useState("overview");

  const loadDashboard = useCallback(async () => {
    if (!selectedSite?.id) return;
    try {
      const res = await api.get(`/websites/${selectedSite.id}/maintenance/`);
      setDashboard(res.data);
    } catch {
      setDashboard(null);
    }
  }, [selectedSite?.id]);

  const loadSuggestions = useCallback(async () => {
    if (!selectedSite?.id) return;
    try {
      const res = await api.get(`/websites/${selectedSite.id}/auto-fix-results/`);
      setSuggestions(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      setSuggestions([]);
    }
  }, [selectedSite?.id]);

  const loadRuns = useCallback(async () => {
    if (!selectedSite?.id) return;
    try {
      const res = await api.get(`/websites/${selectedSite.id}/auto-fix-runs/`);
      setRuns(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      setRuns([]);
    }
  }, [selectedSite?.id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadDashboard(), loadSuggestions(), loadRuns()]).finally(() =>
      setLoading(false)
    );
  }, [loadDashboard, loadSuggestions, loadRuns]);

  const handleTrigger = async (taskType) => {
    setTriggering(taskType);
    try {
      await api.post(`/websites/${selectedSite.id}/maintenance/trigger/`, {
        task_type: taskType,
      });
    } catch {}
    setTimeout(() => {
      loadDashboard();
      loadRuns();
      setTriggering(null);
    }, 2000);
  };

  const handleFix = async (taskType) => {
    const fixKey = `fix_${taskType}`;
    setTriggering(fixKey);
    try {
      if (taskType === "orphan_pages") {
        // Orphans use a dedicated background task
        await api.post(`/websites/${selectedSite.id}/maintenance/trigger/`, {
          task_type: "fix_orphans",
        });
      } else {
        // Meta/titles: approve all pending suggestions of this type
        const fixTypeMap = {
          meta_descriptions: "meta_description",
          title_tags: "title_tag",
        };
        await api.post(
          `/websites/${selectedSite.id}/auto-fix-results/approve-all/`,
          { fix_type: fixTypeMap[taskType] || taskType }
        );
      }
      await loadSuggestions();
      await loadDashboard();
      await loadRuns();
    } catch {}
    setTimeout(() => setTriggering(null), 2000);
  };

  const handleApprove = async (resultId) => {
    setApproving(true);
    try {
      await api.post(
        `/websites/${selectedSite.id}/auto-fix-results/${resultId}/approve/`
      );
      await loadSuggestions();
      await loadDashboard();
    } catch {}
    setApproving(false);
  };

  const handleReject = async (resultId) => {
    setApproving(true);
    try {
      await api.post(
        `/websites/${selectedSite.id}/auto-fix-results/${resultId}/reject/`
      );
      await loadSuggestions();
    } catch {}
    setApproving(false);
  };

  const handleApproveAll = async (fixType) => {
    setApproving(true);
    try {
      await api.post(
        `/websites/${selectedSite.id}/auto-fix-results/approve-all/`,
        { fix_type: fixType }
      );
      await loadSuggestions();
      await loadDashboard();
    } catch {}
    setApproving(false);
  };

  if (!selectedSite) {
    return (
      <div className="p-8 text-center text-gray-500">
        Select a website to view SEO maintenance.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-copper" size={24} />
      </div>
    );
  }

  const pendingSuggestions = suggestions.filter((s) => s.status === "suggested");
  const metaSuggestions = pendingSuggestions.filter((s) => s.fix_type === "meta_description");
  const titleSuggestions = pendingSuggestions.filter((s) => s.fix_type === "title_tag");
  const orphanSuggestions = pendingSuggestions.filter((s) => s.fix_type === "orphan_page");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wrench className="text-copper" size={24} />
          <div>
            <h1 className="text-xl font-bold text-white">SEO Maintenance</h1>
            <p className="text-sm text-gray-500">
              {dashboard?.domain || selectedSite.domain} — Automated SEO fixes
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            loadDashboard();
            loadSuggestions();
            loadRuns();
          }}
          className="flex items-center gap-2 px-3 py-2 border border-dark-50 hover:border-copper text-gray-400 hover:text-copper rounded-lg text-sm transition-colors"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={AlertTriangle}
          label="Pending Suggestions"
          value={dashboard?.pending_suggestions || 0}
          color="text-amber-400"
        />
        <StatCard
          icon={CheckCircle}
          label="Latest Run"
          value={
            dashboard?.recent_activity?.[0]
              ? dashboard.recent_activity[0].status
              : "—"
          }
          color="text-green-400"
        />
        <StatCard
          icon={Zap}
          label="Active Automations"
          value={
            [
              dashboard?.auto_meta_enabled,
              dashboard?.auto_titles_enabled,
              dashboard?.auto_orphans_enabled,
              dashboard?.auto_alt_text_enabled,
              dashboard?.weekly_report_enabled,
            ].filter(Boolean).length
          }
          color="text-copper"
        />
        <StatCard
          icon={FileText}
          label="Total Runs"
          value={runs.length}
          color="text-blue-400"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-dark-50">
        {[
          { key: "overview", label: "Task Overview" },
          { key: "suggestions", label: `Suggestions (${pendingSuggestions.length})` },
          { key: "history", label: "Run History" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-copper text-copper"
                : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TaskCard
            type="meta_descriptions"
            latestRun={dashboard?.latest_runs?.meta_descriptions}
            enabled={dashboard?.auto_meta_enabled}
            onTrigger={handleTrigger}
            onFix={handleFix}
            triggering={triggering}
            pendingCount={metaSuggestions.length}
          />
          <TaskCard
            type="title_tags"
            latestRun={dashboard?.latest_runs?.title_tags}
            enabled={dashboard?.auto_titles_enabled}
            onTrigger={handleTrigger}
            onFix={handleFix}
            triggering={triggering}
            pendingCount={titleSuggestions.length}
          />
          <TaskCard
            type="orphan_pages"
            latestRun={dashboard?.latest_runs?.orphan_pages}
            enabled={dashboard?.auto_orphans_enabled}
            onTrigger={handleTrigger}
            onFix={handleFix}
            triggering={triggering}
            pendingCount={orphanSuggestions.length}
          />
          <TaskCard
            type="weekly_report"
            latestRun={null}
            enabled={dashboard?.weekly_report_enabled}
            onTrigger={handleTrigger}
            onFix={handleFix}
            triggering={triggering}
            pendingCount={0}
          />
        </div>
      )}

      {tab === "suggestions" && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          {/* Bulk Actions */}
          {pendingSuggestions.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-dark-100 border-b border-dark-50">
              <span className="text-sm text-gray-400">
                {pendingSuggestions.length} pending suggestions
              </span>
              <div className="flex-1" />
              {metaSuggestions.length > 0 && (
                <button
                  onClick={() => handleApproveAll("meta_description")}
                  disabled={approving}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-900/20 text-blue-400 hover:bg-blue-900/40 rounded-lg text-xs font-medium transition-colors"
                >
                  <Zap size={12} />
                  Approve All Meta ({metaSuggestions.length})
                </button>
              )}
              {titleSuggestions.length > 0 && (
                <button
                  onClick={() => handleApproveAll("title_tag")}
                  disabled={approving}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-900/20 text-purple-400 hover:bg-purple-900/40 rounded-lg text-xs font-medium transition-colors"
                >
                  <Zap size={12} />
                  Approve All Titles ({titleSuggestions.length})
                </button>
              )}
              {orphanSuggestions.length > 0 && (
                <button
                  onClick={() => handleFix("orphan_pages")}
                  disabled={approving || triggering === "fix_orphan_pages"}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-900/20 text-amber-400 hover:bg-amber-900/40 rounded-lg text-xs font-medium transition-colors"
                >
                  <Wrench size={12} />
                  Fix All Orphans ({orphanSuggestions.length})
                </button>
              )}
            </div>
          )}

          {/* Suggestion List */}
          {suggestions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No suggestions yet. Run a scan to detect issues.
            </div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto">
              {suggestions.map((item) => (
                <SuggestionRow
                  key={item.id}
                  item={item}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  approving={approving}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "history" && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          {runs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No runs yet. Trigger a task above or wait for the scheduled run.
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-50 text-xs text-gray-500 uppercase">
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Found</th>
                  <th className="text-right px-4 py-3">Fixed</th>
                  <th className="text-right px-4 py-3">Skipped</th>
                  <th className="text-right px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  const cfg = FIX_TYPES[run.fix_type] || FIX_TYPES.meta_descriptions;
                  const Icon = cfg.icon;
                  return (
                    <tr
                      key={run.id}
                      className="border-b border-dark-50 last:border-0 hover:bg-dark-100/50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Icon size={14} className={cfg.color} />
                          <span className="text-sm text-white">{cfg.label}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={run.status} />
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-white font-mono">
                        {run.items_found}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-green-400 font-mono">
                        {run.items_fixed}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-500 font-mono">
                        {run.items_skipped}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-gray-500 font-mono">
                        {new Date(run.started_at).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
